import React from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import '../styles/ExpenseDashboard.css';

// Register ChartJS elements
ChartJS.register(ArcElement, Tooltip, Legend);

const ExpenseDashboard = () => {
  // Chart Data Configuration
  const chartData = {
    labels: ['Rent', 'Salary', 'Utilities', 'Transport', 'Marketing', 'Others'],
    datasets: [
      {
        data: [75000, 120000, 24970, 5800, 5250, 68100],
        backgroundColor: [
          '#5c67f2', // Rent (Blue/Purple)
          '#4cd964', // Salary (Green)
          '#ff9500', // Utilities (Orange)
          '#5ac8fa', // Transport (Light Blue)
          '#ff3b30', // Marketing (Red)
          '#af52de', // Others (Purple)
        ],
        borderWidth: 0,
        cutout: '72%',
      },
    ],
  };

  const chartOptions = {
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true }
    },
    maintainAspectRatio: false,
    layout: { padding: 0 }
  };

  const tableData = [
    { id: 1, date: "20 May 2025", title: "Office Rent - May 2025", sub: "Monthly office rent payment", category: "Rent", method: "Bank Transfer", methodIcon: "fa-solid fa-building-columns text-muted", amount: "₹75,000", status: "Paid" },
    { id: 2, date: "20 May 2025", title: "Staff Salary - May 2025", sub: "Salaries for office staff", category: "Salary", method: "Cash", methodIcon: "fa-solid fa-money-bill-1-wave ex-text-green", amount: "₹1,20,000", status: "Paid" },
    { id: 3, date: "19 May 2025", title: "Electricity Bill - Apr 2025", sub: "Office electricity bill", category: "Utilities", method: "UPI", methodIcon: "UPI", isTextIcon: true, amount: "₹8,650", status: "Paid" },
    { id: 4, date: "18 May 2025", title: "Marketing - Facebook Ads", sub: "Facebook marketing expenses", category: "Marketing", method: "UPI", methodIcon: "UPI", isTextIcon: true, amount: "₹5,250", status: "Paid" },
    { id: 5, date: "17 May 2025", title: "Transport - Delivery", sub: "Delivery and transport charges", category: "Transport", method: "Cash", methodIcon: "fa-solid fa-money-bill-1-wave ex-text-green", amount: "₹2,850", status: "Paid" },
    { id: 6, date: "16 May 2025", title: "Office Supplies", sub: "Stationery and other supplies", category: "Office", method: "Cash", methodIcon: "fa-solid fa-money-bill-1-wave ex-text-green", amount: "₹1,950", status: "Paid" },
    { id: 7, date: "15 May 2025", title: "Printer Maintenance", sub: "Printer servicing charges", category: "Maintenance", method: "Bank Transfer", methodIcon: "fa-solid fa-building-columns text-muted", amount: "₹2,150", status: "Paid" },
    { id: 8, date: "14 May 2025", title: "Internet Bill - May 2025", sub: "Monthly internet bill", category: "Utilities", method: "UPI", methodIcon: "UPI", isTextIcon: true, amount: "₹1,320", status: "Paid" },
    { id: 9, date: "13 May 2025", title: "Business Meeting", sub: "Client meeting and refreshments", category: "Other", method: "Cash", methodIcon: "fa-solid fa-money-bill-1-wave ex-text-green", amount: "₹1,250", status: "Paid" },
    { id: 10, date: "12 May 2025", title: "Vehicle Fuel", sub: "Diesel for delivery vehicle", category: "Transport", method: "Cash", methodIcon: "fa-solid fa-money-bill-1-wave ex-text-green", amount: "₹2,950", status: "Paid" }
  ];

  return (
    <div className="ex-dashboard-wrapper">
      {/* Top 5 Metric Cards */}
      <div className="ex-top-grid">
        <div className="ex-card ex-metric-card">
          <div className="ex-metric-icon ex-bg-purple-light"><i className="fa-solid fa-wallet ex-text-purple"></i></div>
          <div className="ex-metric-info">
            <span className="ex-label">Total Expenses</span>
            <h2 className="ex-value">₹3,25,120</h2>
            <div className="ex-meta"><span>This Month</span> <span className="ex-trend-up"><i className="fa-solid fa-arrow-up"></i> 12.5%</span></div>
          </div>
        </div>
        <div className="ex-card ex-metric-card">
          <div className="ex-metric-icon ex-bg-green-light"><i className="fa-regular fa-calendar ex-text-green"></i></div>
          <div className="ex-metric-info">
            <span className="ex-label">Today's Expenses</span>
            <h2 className="ex-value">₹12,450</h2>
            <div className="ex-meta"><span>20 May 2025</span></div>
          </div>
        </div>
        <div className="ex-card ex-metric-card">
          <div className="ex-metric-icon ex-bg-blue-light"><i className="fa-solid fa-chart-column ex-text-blue"></i></div>
          <div className="ex-metric-info">
            <span className="ex-label">This Week Expenses</span>
            <h2 className="ex-value">₹45,780</h2>
            <div className="ex-meta"><span>12 May - 18 May 2025</span></div>
          </div>
        </div>
        <div className="ex-card ex-metric-card">
          <div className="ex-metric-icon ex-bg-orange-light"><i className="fa-solid fa-clock-rotate-left ex-text-orange"></i></div>
          <div className="ex-metric-info">
            <span className="ex-label">This Month Budget</span>
            <h2 className="ex-value">₹5,00,000</h2>
            <div className="ex-meta ex-flex-between"><span>Budget</span> <span className="ex-text-dark">65%</span></div>
            <div className="ex-progress-bar"><div className="ex-progress-fill" style={{ width: '65%' }}></div></div>
          </div>
        </div>
        <div className="ex-card ex-metric-card">
          <div className="ex-metric-icon ex-bg-teal-light"><i className="fa-solid fa-chart-line ex-text-teal"></i></div>
          <div className="ex-metric-info">
            <span className="ex-label">Average Daily Expense</span>
            <h2 className="ex-value">₹16,256</h2>
            <div className="ex-meta"><span>This Month</span></div>
          </div>
        </div>
      </div>

      {/* Main Layout Grid */}
      <div className="ex-main-grid">
        
        {/* Left Side: Table & Filters */}
        <div className="ex-card ex-table-section">
          
          <div className="ex-filters-row">
            <div className="ex-search-box">
              <i className="fa-solid fa-magnifying-glass"></i>
              <input type="text" placeholder="Search by expense name, note..." />
            </div>
            <div className="ex-dropdown"><i className="fa-regular fa-calendar"></i> Select Date</div>
            <div className="ex-dropdown">All Categories <i className="fa-solid fa-chevron-down"></i></div>
            <div className="ex-dropdown">All Payment Methods <i className="fa-solid fa-chevron-down"></i></div>
            <div className="ex-dropdown">All Status <i className="fa-solid fa-chevron-down"></i></div>
          </div>

          <div className="ex-category-pills">
            {['All', 'Rent', 'Salary', 'Transport', 'Utilities', 'Office', 'Marketing', 'Maintenance', 'Other'].map((cat, i) => (
              <button key={i} className={`ex-pill ${cat === 'All' ? 'ex-pill-active' : ''}`}>{cat}</button>
            ))}
          </div>

          <div className="ex-table-container">
            <table className="ex-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Expense Details</th>
                  <th>Category</th>
                  <th>Payment Method</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((row) => (
                  <tr key={row.id}>
                    <td className="ex-text-dark-muted">{row.id}</td>
                    <td className="ex-text-dark-muted">{row.date}</td>
                    <td>
                      <div className="ex-row-title">{row.title}</div>
                      <div className="ex-row-sub">{row.sub}</div>
                    </td>
                    <td><span className={`ex-badge ex-badge-${row.category.toLowerCase()}`}>{row.category}</span></td>
                    <td className="ex-payment-cell">
                      {row.isTextIcon ? <strong className="ex-upi-text">UPI</strong> : <i className={row.methodIcon}></i>} 
                      <span>{row.method}</span>
                    </td>
                    <td className="ex-amount-cell">{row.amount}</td>
                    <td><span className="ex-badge ex-badge-paid">Paid</span></td>
                    <td className="ex-actions-cell">
                      <button className="ex-action-btn"><i className="fa-regular fa-pen-to-square"></i></button>
                      <button className="ex-action-btn ex-btn-delete"><i className="fa-regular fa-trash-can"></i></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="ex-pagination">
            <span className="ex-page-info">Showing 1 to 10 of 48 entries</span>
            <div className="ex-page-controls">
              <button className="ex-page-btn"><i className="fa-solid fa-chevron-left"></i></button>
              <button className="ex-page-btn ex-page-active">1</button>
              <button className="ex-page-btn">2</button>
              <button className="ex-page-btn">3</button>
              <button className="ex-page-btn">4</button>
              <button className="ex-page-btn">5</button>
              <span className="ex-page-dots">...</span>
              <button className="ex-page-btn">5</button>
              <button className="ex-page-btn"><i className="fa-solid fa-chevron-right"></i></button>
            </div>
            <div className="ex-page-size">
              Show <select defaultValue="10"><option value="10">10</option></select> per page
            </div>
          </div>
        </div>

        {/* Right Side: Summaries & Charts */}
        <div className="ex-sidebar">
          
          <div className="ex-card ex-sidebar-card">
            <h3 className="ex-card-title">Expense Summary</h3>
            <ul className="ex-summary-list">
              <li>
                <div className="ex-list-label"><div className="ex-icon-sm ex-bg-purple-light"><i className="fa-solid fa-wallet ex-text-purple"></i></div> Total Expenses (This Month)</div>
                <strong className="ex-list-value">₹3,25,120</strong>
              </li>
              <li>
                <div className="ex-list-label"><div className="ex-icon-sm ex-bg-green-light"><i className="fa-solid fa-check ex-text-green"></i></div> Total Paid</div>
                <strong className="ex-list-value ex-text-green">₹2,95,670</strong>
              </li>
              <li>
                <div className="ex-list-label"><div className="ex-icon-sm ex-bg-red-light"><i className="fa-solid fa-clock ex-text-red"></i></div> Total Unpaid</div>
                <strong className="ex-list-value ex-text-red">₹29,450</strong>
              </li>
              <li>
                <div className="ex-list-label"><div className="ex-icon-sm ex-bg-blue-light"><i className="fa-solid fa-lock ex-text-blue"></i></div> Total Budget</div>
                <strong className="ex-list-value">₹5,00,000</strong>
              </li>
              <li>
                <div className="ex-list-label"><div className="ex-icon-sm ex-bg-orange-light"><i className="fa-solid fa-coins ex-text-orange"></i></div> Remaining Budget</div>
                <strong className="ex-list-value ex-text-blue">₹1,74,880</strong>
              </li>
            </ul>
          </div>

          <div className="ex-card ex-sidebar-card">
            <h3 className="ex-card-title">Expenses by Category</h3>
            <div className="ex-chart-container">
              <div className="ex-chart-wrapper">
                <Doughnut data={chartData} options={chartOptions} />
                <div className="ex-chart-center">
                  <span className="ex-chart-label">Total</span>
                  <strong className="ex-chart-total">₹3,25,120</strong>
                </div>
              </div>
              <div className="ex-legend">
                <div className="ex-legend-item"><span className="ex-dot" style={{backgroundColor: '#5c67f2'}}></span><div className="ex-legend-text"><strong>Rent</strong><span>₹75,000 (23.1%)</span></div></div>
                <div className="ex-legend-item"><span className="ex-dot" style={{backgroundColor: '#4cd964'}}></span><div className="ex-legend-text"><strong>Salary</strong><span>₹1,20,000 (36.9%)</span></div></div>
                <div className="ex-legend-item"><span className="ex-dot" style={{backgroundColor: '#ff9500'}}></span><div className="ex-legend-text"><strong>Utilities</strong><span>₹24,970 (7.7%)</span></div></div>
                <div className="ex-legend-item"><span className="ex-dot" style={{backgroundColor: '#5ac8fa'}}></span><div className="ex-legend-text"><strong>Transport</strong><span>₹5,800 (1.8%)</span></div></div>
                <div className="ex-legend-item"><span className="ex-dot" style={{backgroundColor: '#ff3b30'}}></span><div className="ex-legend-text"><strong>Marketing</strong><span>₹5,250 (1.6%)</span></div></div>
                <div className="ex-legend-item"><span className="ex-dot" style={{backgroundColor: '#af52de'}}></span><div className="ex-legend-text"><strong>Others</strong><span>₹68,100 (20.9%)</span></div></div>
              </div>
            </div>
          </div>

          <div className="ex-card ex-sidebar-card">
            <h3 className="ex-card-title">Recent Categories</h3>
            <ul className="ex-summary-list ex-recent-list">
              <li>
                <div className="ex-list-label"><div className="ex-icon-sm ex-bg-purple-light"><i className="fa-solid fa-building ex-text-purple"></i></div> <span className="ex-text-dark">Rent</span></div>
                <div className="ex-list-right"><strong>₹75,000</strong> <i className="fa-solid fa-chevron-right ex-text-muted"></i></div>
              </li>
              <li>
                <div className="ex-list-label"><div className="ex-icon-sm ex-bg-green-light"><i className="fa-solid fa-users ex-text-green"></i></div> <span className="ex-text-dark">Salary</span></div>
                <div className="ex-list-right"><strong>₹1,20,000</strong> <i className="fa-solid fa-chevron-right ex-text-muted"></i></div>
              </li>
              <li>
                <div className="ex-list-label"><div className="ex-icon-sm ex-bg-orange-light"><i className="fa-solid fa-bolt ex-text-orange"></i></div> <span className="ex-text-dark">Utilities</span></div>
                <div className="ex-list-right"><strong>₹24,970</strong> <i className="fa-solid fa-chevron-right ex-text-muted"></i></div>
              </li>
            </ul>
          </div>

        </div>
      </div>
    </div>
  );
};

export default ExpenseDashboard;